import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Navbar } from './components/layout/Navbar';
import { HeroSection } from './components/home/HeroSection';
import { TrustStrip } from './components/home/TrustStrip';
import { AboutSection } from './components/home/AboutSection';
import { ServicesSection } from './components/home/ServicesSection';
import { ProjectsSection } from './components/home/ProjectsSection';
import { VideoShowcase } from './components/home/VideoShowcase';
import { HowWeWorkSection } from './components/home/HowWeWorkSection';
import { TestimonialsSection } from './components/home/TestimonialsSection';
import { LeadCaptureForm } from './components/home/LeadCaptureForm';
import { BranchesSection } from './components/home/BranchesSection';
import { FinalCtaSection } from './components/home/FinalCtaSection';
import { FAQSection } from './components/home/FAQSection';
import { Footer } from './components/layout/Footer';
import { ChatWidget } from './components/ChatWidget';
import { CaseStudyPage } from './components/CaseStudyPage';
import { LocationPage } from './components/LocationPage';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { TermsOfServicePage } from './components/TermsOfServicePage';
import { useTheme } from './context/ThemeContext';
import { SEO } from './components/SEO';

const HOMEPAGE_SCHEMA = {
  "@type": ["LocalBusiness", "InteriorDesignBusiness"],
  "name": "Aura Spaces",
  "alternateName": "Aura Spaces - A division of Harmony Home Designs",
  "url": "https://www.auraspaces.net",
  "logo": "https://www.auraspaces.net/logo.png",
  "image": "https://www.auraspaces.net/images/hero/hero-1.jpg",
  "description": "Premium interior design studio across Telangana, Andhra Pradesh & Karnataka offering modular kitchens, full home interiors, wardrobes, false ceilings and turnkey projects.",
  "telephone": "+919989697942",
  "email": "operations@auraspaces.net",
  "priceRange": "₹₹₹",
  "openingHours": "Mo-Sa 10:00-19:00",
  "areaServed": ["Hyderabad", "Vijayawada", "Bangalore", "Telangana", "Andhra Pradesh", "Karnataka"],
  "sameAs": [
    "https://www.instagram.com/auraspaces_hhd?igsh=bjlmc2h5Zmt0MjV0",
    "https://www.facebook.com/people/Aura-Spaces/61557697775548/",
    "https://www.linkedin.com/company/aura-spaces-hhd/"
  ]
};

const HOMEPAGE_FAQS = {
  "@type": "FAQPage",
  "mainEntity": [
    {"@type": "Question", "name": "What is the cost of a 2BHK interior?", "acceptedAnswer": {"@type": "Answer", "text": "A complete 2BHK interior starts from ₹3.5L depending on materials and customization. Book a free consultation for a detailed quote tailored to your space."}},
    {"@type": "Question", "name": "Do you provide 3D designs?", "acceptedAnswer": {"@type": "Answer", "text": "Yes! Every project includes free 3D visualization so you can see and approve your design before execution begins."}},
    {"@type": "Question", "name": "What is your delivery timeline?", "acceptedAnswer": {"@type": "Answer", "text": "Projects are completed in 45-60 days depending on the size — residential projects tend to be quicker while commercial projects may take the full duration. We guarantee on-time delivery with a dedicated project manager."}},
    {"@type": "Question", "name": "Do you work in my city?", "acceptedAnswer": {"@type": "Answer", "text": "We currently operate in major cities across Telangana, Andhra Pradesh, and Karnataka with full design studios and manufacturing hubs in all three states."}},
    {"@type": "Question", "name": "How do I get started?", "acceptedAnswer": {"@type": "Answer", "text": "Simply fill our consultation form or call us at 9989697942. We'll schedule a free site visit within 48 hours."}},
    {"@type": "Question", "name": "What materials do you use?", "acceptedAnswer": {"@type": "Answer", "text": "We use premium brands across every category — Laminates: Merino, Century, Advance, Virgo, Royale Touch, Greenply. Plywood: Greenply, Century, Merino, Duroply. Hardware: Hettich, Ebco, Hafele, Godrej & Blum (Luxury). Paints: Asian, Berger, Nerolac, Dulux. Tiles: RAK Ceramics, Somany, Millennium, Kajaria (Luxury). Sanitary: Jaquar, Cera, Grohe, Queo, Kohler (Luxury). Lights: Havells, Philips, Wipro, Orient, Panasonic. Switches: Havells, Legrand, L&T, Norisys (Luxury). Cables: KEI, Polycab, Havells, Finolex."}},
    {"@type": "Question", "name": "Is there a warranty on your work?", "acceptedAnswer": {"@type": "Answer", "text": "Absolutely. We provide a comprehensive 5-year warranty on all woodwork and modular fittings, plus lifetime service support for our clients."}}
  ]
};

const COMPOSITE_HOMEPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    HOMEPAGE_SCHEMA,
    HOMEPAGE_FAQS
  ]
};

function HomePage() {
  return (
    <>
      <SEO 
        title="Aura Spaces | Premium Interior Design in Hyderabad, Vijayawada & Bangalore"
        description="Aura Spaces — award-winning interior designers for premium homes and modern office spaces in Hyderabad, Vijayawada & Bangalore. Home interiors starting from ₹3.5L. Modular kitchens, wardrobes, and commercial turnkey projects."
        keywords="interior design Hyderabad, interior designers Vijayawada, interior design Bangalore, 2BHK interior cost, 3BHK interior price, modular kitchen packages, luxury villa interiors, office design Hyderabad, commercial interiors, Tellapur, Gachibowli, Narsingi, Kokapet, Whitefield, Sarjapur, Aura Spaces"
        geoRegion="IN-TG"
        geoPlacename="Hyderabad"
        schema={COMPOSITE_HOMEPAGE_SCHEMA}
      />
      <HeroSection />
      <TrustStrip />
      <AboutSection />
      <ServicesSection />
      <ProjectsSection />
      {/* <VideoShowcase /> -- Commented out: enable when proper walkthrough video asset is ready */}
      <HowWeWorkSection />
      <TestimonialsSection />
      <LeadCaptureForm />
      <BranchesSection />
      <FAQSection />
      <FinalCtaSection />
    </>
  );
}

export default function App() {
  const { theme } = useTheme();
  return (
    <div className={`font-sans antialiased transition-colors duration-500 ${
      theme === 'dark'
        ? 'bg-[#0B0F1A] text-gray-200'
        : 'bg-bg-light text-gray-900'
    } selection:bg-accent selection:text-white`}>
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/interior-designers-in-hyderabad" element={<LocationPage city="Hyderabad" />} />
          <Route path="/interior-designers-in-vijayawada" element={<LocationPage city="Vijayawada" />} />
          <Route path="/interior-designers-in-bangalore" element={<LocationPage city="Bangalore" />} />
          <Route path="/case-study/:slug" element={<CaseStudyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        </Routes>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
